import {
  ChronoUnit,
  Instant,
  LocalDate,
  LocalTime,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import "@js-joda/timezone";
import { Text } from "components/Typography/text.styled";
import { FontFamily, FontSize } from "config/font";
import { CompanyGigShift, GigType, PaymentMethod } from "model/Gig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ButtonComponent } from "../../general/button/button.component";
import { DatePickerWithLabel } from "../../general/datePickerWithLabel/datePickerWithLabel.component";
import { TextInputWithLabel } from "../../general/textInput/textInput.component";
import { TimePickerWithLabel } from "../../general/timePicker/timePickerWithLabel.component";
import { SimpleRow } from "../../organizationalStructure/organizationalStructure.styled";
import { DateContainer } from "./createGigModal.styled";
import { Worker } from "../../organizationalStructure/chooseWorkersModal.component";

export type ShiftsFormData = {
  startTime: string;
  endTime: string;
  breakTimeMin: number;
  breakStartTime: string;
  breakEndTime: string;
  deductionAmount: number;
};

type Props = {
  setFormData: React.Dispatch<
    React.SetStateAction<{
      description: string;
      shifts: CompanyGigShift[];
      type: GigType;
    }>
  >;
  selectedWorkers: Worker[];
};

const safeParseInt = (val: string | undefined, fallback = 0) => {
  const n = Number.parseInt(val ?? "", 10);
  return Number.isFinite(n) ? n : fallback;
};

const parseToLocalTime = (timeStr: string, zone: ZoneId): LocalTime => {
  if (!timeStr) throw new Error("Invalid time");

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
    return LocalTime.parse(timeStr);
  }

  try {
    const zdt = ZonedDateTime.parse(timeStr);
    return zdt.withZoneSameInstant(zone).toLocalTime();
  } catch {}

  try {
    const inst = Instant.parse(timeStr);
    return ZonedDateTime.ofInstant(inst, zone).toLocalTime();
  } catch {}

  throw new Error(`Unsupported time format: ${timeStr}`);
};

const createZonedDateTime = (
  date: LocalDate,
  timeStr: string,
  zone: ZoneId,
): ZonedDateTime => date.atTime(parseToLocalTime(timeStr, zone)).atZone(zone);

const minutesBetween = (start: ZonedDateTime, end: ZonedDateTime): number =>
  Math.max(0, Math.floor(start.until(end, ChronoUnit.MINUTES)));

const toLocalDateInZone = (d: Date, zone: ZoneId): LocalDate =>
  ZonedDateTime.ofInstant(
    Instant.ofEpochMilli(d.getTime()),
    zone,
  ).toLocalDate();

const getCompanySetting = (
  settings: any[] | undefined,
  key: string,
  fallback: string | number,
) => settings?.find((s: any) => s.key === key)?.value ?? fallback;

const shiftsOverlap = (a: CompanyGigShift, b: CompanyGigShift): boolean => {
  if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) return false;

  const aStart = Instant.parse(a.startTime);
  const aEnd = Instant.parse(a.endTime);
  const bStart = Instant.parse(b.startTime);
  const bEnd = Instant.parse(b.endTime);

  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
};

export const ShiftForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();

  const companySettings: any[] =
    companyState.companySettings ?? companyState.company?.companySettings ?? [];

  const companyTimeZone = useMemo(
    () =>
      (getCompanySetting(
        companySettings,
        "CompanyTimeZone",
        "Europe/Stockholm",
      ) as string) || "Europe/Stockholm",
    [companySettings],
  );

  const companyDeductionAmount = useMemo(
    () => Number(getCompanySetting(companySettings, "GigPayDeduction", 0)) || 0,
    [companySettings],
  );

  const companyBreakTime = useMemo(
    () => Number(getCompanySetting(companySettings, "GigBreakTime", 0)) || 0,
    [companySettings],
  );

  const companyGigShiftStartTime = useMemo(
    () =>
      String(
        getCompanySetting(companySettings, "CompanyGigShiftStartTime", "08:00"),
      ) || "08:00",
    [companySettings],
  );

  const companyGigShiftEndTime = useMemo(
    () =>
      String(
        getCompanySetting(companySettings, "CompanyGigShiftEndTime", "17:00"),
      ) || "17:00",
    [companySettings],
  );

  const zoneId = useMemo(() => ZoneId.of(companyTimeZone), [companyTimeZone]);
  const nowTime = useMemo(() => ZonedDateTime.now(zoneId), [zoneId]);
  const [showTimeIntervall, setShowTimeIntervall] = useState(false);
  const [shiftDates, setShiftDates] = useState<LocalDate[]>([]);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const endsSameDay = useCallback(
    (startTime: string, endTime: string) => {
      const st = parseToLocalTime(startTime, zoneId);
      const et = parseToLocalTime(endTime, zoneId);
      return !et.isBefore(st);
    },
    [zoneId],
  );

  const initialStartISO = useMemo(
    () =>
      createZonedDateTime(
        nowTime.toLocalDate(),
        companyGigShiftStartTime,
        zoneId,
      )
        .toInstant()
        .toString(),
    [nowTime, zoneId, companyGigShiftStartTime],
  );

  const initialEndISO = useMemo(
    () =>
      createZonedDateTime(nowTime.toLocalDate(), companyGigShiftEndTime, zoneId)
        .toInstant()
        .toString(),
    [nowTime, zoneId, companyGigShiftEndTime],
  );

  const [formData, setFormData] = useState<ShiftsFormData>({
    startTime: initialStartISO,
    endTime: initialEndISO,
    breakTimeMin: companyBreakTime,
    breakStartTime: nowTime
      .plusHours(1)
      .truncatedTo(ChronoUnit.HOURS)
      .toString(),
    breakEndTime: nowTime.plusHours(1).truncatedTo(ChronoUnit.HOURS).toString(),
    deductionAmount: companyDeductionAmount,
  });

  const calculateBreakMinutes = useCallback(
    (
      date: LocalDate,
      breakStartTime?: string,
      breakEndTime?: string,
    ): number => {
      if (!breakStartTime || !breakEndTime) return 0;

      const brkStartZdt = createZonedDateTime(date, breakStartTime, zoneId);
      const brkEndDay = endsSameDay(breakStartTime, breakEndTime)
        ? date
        : date.plusDays(1);
      const brkEndZdt = createZonedDateTime(brkEndDay, breakEndTime, zoneId);

      return minutesBetween(brkStartZdt, brkEndZdt);
    },
    [zoneId, endsSameDay],
  );

  useEffect(() => {
    if (!showTimeIntervall) return;
    const { breakStartTime, breakEndTime } = formData;
    if (!breakStartTime || !breakEndTime) return;

    const baseDate = shiftDates[0] ?? nowTime.toLocalDate();
    const newValue =
      breakStartTime === breakEndTime
        ? 0
        : calculateBreakMinutes(baseDate, breakStartTime, breakEndTime);

    setFormData((prev) =>
      prev.breakTimeMin !== newValue
        ? { ...prev, breakTimeMin: newValue }
        : prev,
    );
  }, [
    showTimeIntervall,
    formData.breakStartTime,
    formData.breakEndTime,
    shiftDates,
    nowTime,
    calculateBreakMinutes,
  ]);

  const handleCreateShifts = useCallback(() => {
    const newShifts: CompanyGigShift[] = shiftDates.map((d: LocalDate) => {
      const endDay = endsSameDay(formData.startTime, formData.endTime)
        ? d
        : d.plusDays(1);

      const startZdt = createZonedDateTime(d, formData.startTime, zoneId);
      const endZdt = createZonedDateTime(endDay, formData.endTime, zoneId);

      const shift: CompanyGigShift = {
        startTime: startZdt.toInstant().toString(),
        endTime: endZdt.toInstant().toString(),
        deductionAmount: formData.deductionAmount,
      };

      if (
        formData.breakStartTime &&
        formData.breakEndTime &&
        formData.breakStartTime !== formData.breakEndTime
      ) {
        const brkEndDay = endsSameDay(
          formData.breakStartTime,
          formData.breakEndTime,
        )
          ? d
          : d.plusDays(1);
        const breakStartZdt = createZonedDateTime(
          d,
          formData.breakStartTime,
          zoneId,
        );
        const breakEndZdt = createZonedDateTime(
          brkEndDay,
          formData.breakEndTime,
          zoneId,
        );

        shift.breakStartTime = breakStartZdt.toInstant().toString();
        shift.breakEndTime = breakEndZdt.toInstant().toString();
      }

      if (formData.breakTimeMin && formData.breakTimeMin > 0) {
        shift.breakTime = formData.breakTimeMin;
      }

      return shift;
    });

    props.setFormData((prev) => {
      const filteredNewShifts = newShifts.filter(
        (newShift) =>
          !prev.shifts.some((existingShift) =>
            shiftsOverlap(existingShift, newShift),
          ),
      );

      if (filteredNewShifts.length === 0) {
        return prev;
      }

      return {
        ...prev,
        shifts: [...prev.shifts, ...filteredNewShifts],
      };
    });

    setShiftDates([]);
    setDatePickerResetKey((k) => k + 1);
  }, [shiftDates, formData, zoneId, endsSameDay, props]);

  const validateBreakWithinShift = useCallback((): boolean => {
    const { startTime, endTime, breakStartTime, breakEndTime, breakTimeMin } =
      formData;

    if (!showTimeIntervall) return (breakTimeMin ?? 0) >= 0;

    if (!breakStartTime || !breakEndTime) return true;

    if (breakStartTime === breakEndTime) return (breakTimeMin ?? 0) >= 0;

    const baseDate = shiftDates[0] ?? nowTime.toLocalDate();

    const shiftEndDay = endsSameDay(startTime, endTime)
      ? baseDate
      : baseDate.plusDays(1);
    const shiftStartZdt = createZonedDateTime(baseDate, startTime, zoneId);
    const shiftEndZdt = createZonedDateTime(shiftEndDay, endTime, zoneId);

    const brkEndDay = endsSameDay(breakStartTime, breakEndTime)
      ? baseDate
      : baseDate.plusDays(1);
    const brkStartZdt = createZonedDateTime(baseDate, breakStartTime, zoneId);
    const brkEndZdt = createZonedDateTime(brkEndDay, breakEndTime, zoneId);

    if (!brkEndZdt.isAfter(brkStartZdt)) return false;
    if (brkStartZdt.isBefore(shiftStartZdt)) return false;
    if (brkEndZdt.isAfter(shiftEndZdt)) return false;
    if ((breakTimeMin ?? 0) < 0) return false;

    return true;
  }, [formData, showTimeIntervall, shiftDates, nowTime, zoneId, endsSameDay]);

  /** ---------- Datumregel (tidigaste valbara) ----------
   *  Alla Spay            => föregående månad tillåts t.o.m 4:e (låses från 5:e 00:00)
   *  Alla egen lönehantering => föregående månad tillåts t.o.m 19:e (låses från 20:e 00:00)
   *  Blandat              => 4:e-regeln
   */
  const getEarliestByFourthRuleTZ = useCallback((): Date => {
    const now = ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS);

    const workers = props.selectedWorkers ?? [];

    const anySpay = workers.some(
      (w) =>
        w.employmentInfo.current.paymentType === PaymentMethod.SIMFLEXITY_PAY,
    );
    const anyOwn = workers.some(
      (w) =>
        w.employmentInfo.current.paymentType !== PaymentMethod.SIMFLEXITY_PAY,
    );

    const cutoffDay = !workers.length
      ? 4
      : anySpay && anyOwn
        ? 4
        : anySpay
          ? 4
          : 19;

    const lockFrom = now
      .withDayOfMonth(cutoffDay + 1)
      .truncatedTo(ChronoUnit.DAYS);

    const minZdt = (
      now.isBefore(lockFrom)
        ? now.minusMonths(1).withDayOfMonth(1)
        : now.withDayOfMonth(1)
    ).truncatedTo(ChronoUnit.DAYS);

    return new Date(
      minZdt.year(),
      minZdt.monthValue() - 1,
      minZdt.dayOfMonth(),
    );
  }, [zoneId, props.selectedWorkers]);

  return (
    <>
      <DatePickerWithLabel
        key={datePickerResetKey}
        label={t("CreateShift.Date")}
        required
        value={
          shiftDates[0]
            ? new Date(
                shiftDates[0].year(),
                shiftDates[0].monthValue() - 1,
                shiftDates[0].dayOfMonth(),
              )
            : null
        }
        onRangeChange={(dates: Date[]) => {
          setShiftDates(dates.map((d) => toLocalDateInZone(d, zoneId)));
        }}
        placeholder={t("OrganizationalStructure.ChooseDate")}
        dateFormat="YYYY-MM-DD"
        allowRangePicker
        earliestSelectableDate={getEarliestByFourthRuleTZ()}
      />

      <SimpleRow>
        <TimePickerWithLabel
          label={t("StampClock.StartTime")}
          value={formData.startTime}
          onChange={(value) => setFormData((p) => ({ ...p, startTime: value }))}
          required
        />
        <TimePickerWithLabel
          label={t("StampClock.EndTime")}
          value={formData.endTime}
          onChange={(value) => setFormData((p) => ({ ...p, endTime: value }))}
          required
        />
      </SimpleRow>

      <SimpleRow>
        <TextInputWithLabel
          label={t("CreateShift.DeductionAmount")}
          value={String(formData.deductionAmount)}
          onChange={(e) => {
            const v = safeParseInt(e.target.value, 0);
            setFormData((p) => ({ ...p, deductionAmount: Math.max(0, v) }));
          }}
          type="number"
          marginBottom={15}
        />

        {!showTimeIntervall && (
          <TextInputWithLabel
            label={t("StampClock.Break")}
            value={
              formData.breakTimeMin != null ? String(formData.breakTimeMin) : ""
            }
            onChange={(e) => {
              const v = safeParseInt(e.target.value, 0);
              setFormData((p) => ({ ...p, breakTimeMin: Math.max(0, v) }));
            }}
            type="number"
            marginRight={10}
            marginBottom={15}
          />
        )}
      </SimpleRow>

      <Text
        style={{ cursor: "pointer" }}
        fontSize={FontSize.Small}
        fontFamily={FontFamily.MontserratSemiBold}
        onClick={() => setShowTimeIntervall((s) => !s)}
      >
        {!showTimeIntervall
          ? t("CreateShift.SetTimeIntervall")
          : t("CreateShift.ClickToEnterMinutesBreak")}
      </Text>

      {showTimeIntervall && (
        <DateContainer style={{ marginBottom: 30 }}>
          <TimePickerWithLabel
            label={t("StampClock.StartTime")}
            value={formData.breakStartTime}
            onChange={(value) =>
              setFormData((p) => ({ ...p, breakStartTime: value }))
            }
          />
          <TimePickerWithLabel
            label={t("StampClock.EndTime")}
            value={formData.breakEndTime}
            onChange={(value) =>
              setFormData((p) => ({ ...p, breakEndTime: value }))
            }
          />
        </DateContainer>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <ButtonComponent
          size="small"
          onClick={handleCreateShifts}
          action
          marginBottom={20}
          width={200}
          disabled={shiftDates.length === 0 || !validateBreakWithinShift()}
        >
          {t("CreateShift.AddShift")}
        </ButtonComponent>
      </div>
    </>
  );
};
